/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { JSX, Fragment } from 'preact';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { classNames } from '../utils/UNSAFE_classNames';
import { useTooltip } from '../hooks/UNSAFE_useTooltip';

type IntrinsicProps = JSX.SVGAttributes<SVGSVGElement>;

type Props = Pick<IntrinsicProps, 'viewBox' | 'children'> & {
  /**
   * The size of the component. Defaults to '1em', which means the font size of the parent is used.
   */
  size?: keyof typeof sizeStyles;
  /**
   * The color of the component. Defaults to 'currentColor', which behaves like the CSS 'currentColor' value.
   */
  color?: keyof typeof colorStyles;
  /**
   * A label to be used for accessibility purposes.
   */
  accessibleLabel?: string;
};

export const Icon = ({
  size = '1em',
  color = 'currentColor',
  accessibleLabel = '',
  viewBox = 'none',
  children
}: Props) => (
  <Svg
    height="1em"
    width="1em"
    viewBox={viewBox}
    accessibleLabel={accessibleLabel}
    class={classNames([colorStyles[color], sizeStyles[size]])}>
    {children}
  </Svg>
);

type BaseSvgProps = Pick<IntrinsicProps, 'height' | 'width' | 'viewBox' | 'class' | 'children'>;
type SvgProps = BaseSvgProps & {
  accessibleLabel: string;
};

const Svg = ({ accessibleLabel, ...otherProps }: SvgProps) =>
  accessibleLabel ? (
    <SemanticSvg accessibleLabel={accessibleLabel} {...otherProps} />
  ) : (
    <DecorativeSvg {...otherProps} />
  );

const DecorativeSvg = (props: BaseSvgProps) => <svg {...props} />;

const SemanticSvg = ({ accessibleLabel, children, ...props }: SvgProps) => {
  const { tooltipContent, tooltipProps } = useTooltip({
    text: accessibleLabel,
    anchor: {
      x: 'element',
      y: 'element'
    },
    position: 'bottom',
    offset: {
      mainAxis: 8, // redwood tooltip design spec
      crossAxis: 0
    }
  });

  return (
    <Fragment>
      <svg role="img" {...props} {...tooltipProps} aria-label={accessibleLabel}>
        {children}
      </svg>
      {tooltipContent}
    </Fragment>
  );
};

// TODO: Remove this and refactor as necessary to uptake
// the dimensions type and values when they're available.
// See JET-50717
const sizeStyles = {
  '1em': cssProps`${{
    fontSize: '1em'
  }}`,
  '1x': cssProps`${{
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-1x)'
  }}`,
  '2x': cssProps`${{
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-2x)'
  }}`,
  '3x': cssProps`${{
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-3x)'
  }}`,
  '4x': cssProps`${{
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-4x)'
  }}`,
  '5x': cssProps`${{
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-5x)'
  }}`,
  '6x': cssProps`${{
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-6x)'
  }}`,
  '7x': cssProps`${{
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-7x)'
  }}`,
  '8x': cssProps`${{
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-8x)'
  }}`,
  '9x': cssProps`${{
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-9x)'
  }}`,
  '10x': cssProps`${{
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-10x)'
  }}`,
  '11x': cssProps`${{
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-11x)'
  }}`,
  '12x': cssProps`${{
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-12x)'
  }}`
};

const colorStyles = {
  primary: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-primary)'
  }}`,
  secondary: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-secondary)'
  }}`,
  disabled: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-disabled)'
  }}`,
  danger: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-danger-3)'
  }}`,
  warning: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-warning-3)'
  }}`,
  success: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-success-3)'
  }}`,
  info: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-info-3)'
  }}`,
  currentColor: cssProps`${{
    color: 'currentColor'
  }}`
};
